import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { loadStripe, PaymentIntentResult, SetupIntent, Stripe, StripeElements } from '@stripe/stripe-js';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  combineLatestWith,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from "rxjs";

import { environment } from "@environment/environment";
import { ACTIVE_USER } from "@modules/auth/providers/auth.provider";
import { User } from "@modules/user/models/user.model";
import { SnackBarService } from "@shared/services/snack-bar.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PaymentService } from "@shared/component/payments/services/payment.service";
import { PaymentProviderAccountStatusEnum, PaymentProviderNameEnum } from "@generated/graphql";
import { PaymentProviderAccountService } from "@modules/payment/services/payment-provider-account.service";
import { PaymentProviderAccount } from "@modules/payment/models/payment-provider-account.model";
import { CalloutComponent } from "@shared/component/message/callout/callout.component";
import { FormsModule } from "@angular/forms";
import { AsyncPipe, NgForOf, NgIf, NgOptimizedImage, NgTemplateOutlet } from "@angular/common";
import { BillingAddress } from "@modules/address/models/billing-address.model";
import { MatInputModule } from "@angular/material/input";
import { LetModule } from "@ngrx/component";
import { PaymentMethod } from "@modules/payment/models/payment-method.model";
import { PaymentMethodService } from "@modules/payment/services/payment-method.service";
import { billingDetailsAdapter } from "@modules/address/adapters/billing-details.adapter";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import {
  StripeSetupFormComponent
} from "@modules/payment/components/setup-forms/stripe-setup-form/stripe-setup-form.component";
import { MatRadioModule } from "@angular/material/radio";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { Order } from "@modules/order/models/order.model";
import { startWith } from "rxjs/operators";
import { ErrorService } from "@shared/services/error.service";
import { CheckoutFlowFacadeService } from '@shared/component/payments/services/checkout-flow-facade.service';

interface PaymentMethodsState {
  methods: PaymentMethod[];
  loaded: boolean;
}

interface StripeElementContext {
  order: Order;
  promotionId: string | null;
  signature: string;
}

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-stripe-payment-form',
  templateUrl: './stripe-payment-form.component.html',
  styleUrls: ['./stripe-payment-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CalloutComponent,
    FormsModule,
    NgTemplateOutlet,
    AsyncPipe,
    MatInputModule,
    NgIf,
    LetModule,
    NgForOf,
    NgOptimizedImage,
    MatDialogModule,
    StripeSetupFormComponent,
    MatRadioModule,
    MatButtonModule,
    MatIconModule
  ]
})
export class StripePaymentFormComponent implements OnInit, OnDestroy {
  linkAuthenticationElementId = "link-authentication-element";
  paymentElementId = 'payment-element';
  private _loading$ = new BehaviorSubject<boolean>(false);
  private readonly disabledSubject = new BehaviorSubject<boolean>(false);
  private readonly stripeElementReadySubject = new BehaviorSubject<boolean>(false);
  private readonly latestPreparedPaymentIdSubject = new BehaviorSubject<string | null>(null);
  paymentMethodsStateSubject = new BehaviorSubject<PaymentMethodsState>({
    methods: [],
    loaded: false,
  });
  readonly canAcceptPayment$ = this.checkCanAcceptPayment().pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );
  paymentMethods$: Observable<PaymentMethod[]> = this.paymentMethodsStateSubject.asObservable().pipe(
    map((state) => state.methods),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  private canMakePayment$: Observable<boolean> = this.checkoutFlowFacade.currentOrder$.pipe(
    map(order => !!order?.payment?.canMakePayment),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  private paymentMethodsLoaded$: Observable<boolean> = this.paymentMethodsStateSubject.asObservable().pipe(
    map((state) => state.loaded),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  private hasSavedPaymentMethods$: Observable<boolean> = this.paymentMethods$.pipe(
    map((paymentMethods) => paymentMethods.length > 0),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  private paymentSubmissionReady$: Observable<boolean> = combineLatest([
    this.hasSavedPaymentMethods$,
    this.stripeElementReadySubject.asObservable(),
  ]).pipe(
    map(([hasSavedPaymentMethods, stripeElementReady]) => hasSavedPaymentMethods || stripeElementReady),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  private readonly stripeElementContext$: Observable<StripeElementContext> = combineLatest([
    this.checkoutFlowFacade.currentOrder$.pipe(filter((order): order is Order => !!order)),
    this.checkoutFlowFacade.activePromotionId$.pipe(startWith(null)),
    this.hasSavedPaymentMethods$,
    this.paymentMethodsLoaded$,
    this.canAcceptPayment$.pipe(startWith(false)),
  ]).pipe(
    filter(([, , hasSavedPaymentMethods, paymentMethodsLoaded, canAcceptPayment]) => paymentMethodsLoaded && canAcceptPayment && !hasSavedPaymentMethods),
    map(([order, promotionId]) => this.createStripeElementContext(order, promotionId)),
    distinctUntilChanged((previous, current) => previous.signature === current.signature),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  selectedPaymentMethod: PaymentMethod;
  @ViewChild('addCardDialog') private addCardDialog: TemplateRef<any>;
  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;

  constructor(
    private readonly snackBarService: SnackBarService,
    private readonly paymentService: PaymentService,
    private readonly paymentProviderAccountService: PaymentProviderAccountService,
    @Inject(ACTIVE_USER) private readonly activeUser$: Observable<User>,
    private readonly checkoutFlowFacade: CheckoutFlowFacadeService,
    private readonly paymentMethodService: PaymentMethodService,
    private readonly dialog: MatDialog,
    private readonly errorService: ErrorService,
  ) {
  }

  get loading$() {
    return this._loading$.asObservable();
  }

  @Input() set disabled(value: boolean) {
    this.disabledSubject.next(!!value);
  }
  get disabled(): boolean {
    return this.disabledSubject.value;
  }

  get selectedBillingAddress$() {
    return this.checkoutFlowFacade.selectedBillingAddress$;
  }

  get invalid$() {
    return this.selectedBillingAddress$.pipe(
      combineLatestWith(this.loading$),
      combineLatestWith(this.disabledSubject),
      combineLatestWith(this.canMakePayment$),
      combineLatestWith(this.paymentSubmissionReady$),
      map(([[[[selectedBillingAddress, loading], disabled], canMakePayment], paymentSubmissionReady]) => !(selectedBillingAddress instanceof BillingAddress) || loading || disabled || !canMakePayment || !paymentSubmissionReady)
    );
  }

  ngOnInit() {
    this.watchPreparedPaymentElement();
    this.watchPaymentMethodInitialization();
  }

  async makePayment() {
    this.setLoading(true);
    this.snackBarService.pushAlert('Processing your payment. Please wait...', 'Processing');

    let dialogPaymentId: string | null = null;
    let currentOrderId: string | null = null;

    this.checkoutFlowFacade.currentOrder$.pipe(
      take(1),
      switchMap(order => {
        if (!order) {
          this.snackBarService.pushErrorMessage();
          return EMPTY;
        }

        currentOrderId = order.id;
        dialogPaymentId = order?.payment?.id ?? null;
        this.paymentService.markOrderProcessing(order.id);
        this.paymentService.markOrderAwaitingRefresh(order.id);
        if (dialogPaymentId) {
          this.paymentService.markProcessing(dialogPaymentId);
        }

        const payment$ = this.selectedPaymentMethod
          ? this.makePaymentWithSavedMethod$(order)
          : this.makePaymentWithNewMethod$(order);

        return payment$.pipe(
          map(({ result, preparedPaymentId }) => ({
            result,
            dialogPaymentId,
            preparedPaymentId,
          }))
        );
      }),
      tap(({ result, dialogPaymentId: currentDialogPaymentId, preparedPaymentId }) => {
        const { error, paymentIntent } = result;
        const authoritativePaymentId = preparedPaymentId ?? currentDialogPaymentId;
        const trackedPaymentIds = Array.from(
          new Set([currentDialogPaymentId, authoritativePaymentId].filter((paymentId): paymentId is string => Boolean(paymentId)))
        );

        if (error) {
          if (currentOrderId) {
            this.paymentService.clearOrderProcessing(currentOrderId);
            this.paymentService.clearOrderAwaitingRefresh(currentOrderId);
          }
          trackedPaymentIds.forEach(paymentId => this.paymentService.clearProcessing(paymentId));
          return;
        }

        if (paymentIntent?.status === 'succeeded') {
          if (currentDialogPaymentId || authoritativePaymentId) {
            this.paymentService.paid$.next({ paymentId: currentDialogPaymentId ?? authoritativePaymentId! });
          }
          return;
        }

        if (paymentIntent && this.isPaymentIntentInFlight(paymentIntent.status)) {
          trackedPaymentIds.forEach(paymentId => this.paymentService.markProcessing(paymentId));

          if (currentDialogPaymentId || authoritativePaymentId) {
            this.paymentService.processing$.next({ paymentId: currentDialogPaymentId ?? authoritativePaymentId! });
          }
          return;
        }

        if (currentOrderId) {
          this.paymentService.clearOrderProcessing(currentOrderId);
          this.paymentService.clearOrderAwaitingRefresh(currentOrderId);
        }
        trackedPaymentIds.forEach(paymentId => this.paymentService.clearProcessing(paymentId));
      }),
      catchError((error) => {
        if (currentOrderId) {
          this.paymentService.clearOrderProcessing(currentOrderId);
          this.paymentService.clearOrderAwaitingRefresh(currentOrderId);
        }
        if (dialogPaymentId) {
          this.paymentService.clearProcessing(dialogPaymentId);
        }
        const graphQLErrors = error?.graphQLErrors ?? [];
        const messages = this.errorService.getValidationErrorMessages(graphQLErrors);

        if (messages.length) {
          this.snackBarService.pushErrorAlert(messages[0]);
        } else {
          this.snackBarService.pushErrorMessage();
        }
        return EMPTY;
      }),
      finalize(() => this.setLoading(false)),
      take(1)
    ).subscribe();
  }

  private makePaymentWithSavedMethod$(order: Order): Observable<{ result: PaymentIntentResult; preparedPaymentId: string | null }> {
    return this.loadStripeInstance$().pipe(
      switchMap(() => this.prepareStripePayment$(order, this.selectedPaymentMethod.id)),
      switchMap((preparedPayment) => this.confirmPreparedPayment$(preparedPayment.clientSecret).pipe(
        map((result) => ({
          result,
          preparedPaymentId: preparedPayment.paymentId,
        }))
      ))
    );
  }

  private makePaymentWithNewMethod$(_order: Order): Observable<{ result: PaymentIntentResult; preparedPaymentId: string | null }> {
    return this.confirmPayment$().pipe(
      map((result) => ({
        result,
        preparedPaymentId: this.latestPreparedPaymentIdSubject.value,
      }))
    );
  }

  selectPaymentMethod(paymentMethod: PaymentMethod) {
    this.selectedPaymentMethod = paymentMethod;
  }

  addNewPaymentMethod() {
    this.dialog.open(this.addCardDialog, {});
  }

  onSetupSuccess(setupIntent: SetupIntent) {
    this.paymentMethodService.syncAndGetPaymentMethod(setupIntent.payment_method.toString(), PaymentProviderNameEnum.Stripe)
      .subscribe((paymentMethod: PaymentMethod) => {
        this.paymentMethodsStateSubject.next({
          methods: [...this.paymentMethodsStateSubject.value.methods, paymentMethod],
          loaded: true,
        });
        this.stripeElementReadySubject.next(false);
        this.destroyStripeElements();
        this.selectPaymentMethod(paymentMethod);
        this.dialog.closeAll();
        this.snackBarService.pushSuccessAlert('Payment method added successfully');
      });
  }

  private confirmPayment$(clientSecret?: string): Observable<PaymentIntentResult> {
    return combineLatest([
      this.activeUser$.pipe(take(1)),
      this.selectedBillingAddress$.pipe(take(1)),
      this.checkoutFlowFacade.returnUrl$.pipe(take(1)),
    ]).pipe(
      tap(_ => this.setLoading(true)),
      switchMap(([user, selectedBillingAddress, returnUrl]) => {
        if (!this.stripe) {
          this.snackBarService.pushErrorMessage();
          this.setLoading(false);

          return EMPTY;
        }

        const confirmParams: Record<string, any> = {
          return_url: returnUrl,
          receipt_email: user.email,
        };

        if (selectedBillingAddress instanceof BillingAddress) {
          confirmParams.payment_method_data = {
            ...confirmParams.payment_method_data,
            billing_details: billingDetailsAdapter.create(selectedBillingAddress)
          };
        }

        if (clientSecret) {
          return this.stripe.confirmPayment({
            clientSecret,
            confirmParams,
            redirect: 'if_required' as any,
          });
        }

        if (!this.elements) {
          this.snackBarService.pushErrorMessage();
          this.setLoading(false);

          return EMPTY;
        }

        return this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams,
          redirect: 'if_required' as any,
        });
      }),
      tap(({ error }) => {
        if (!error) {
          return;
        }

        if (error.type === "card_error" || error.type === "validation_error") {
          this.snackBarService.pushAlert(error.message);
        } else {
          this.snackBarService.pushErrorMessage();
        }

        this.setLoading(false);
      })
    )
  }

  private getPaymentMethods(): Observable<PaymentMethod[]> {
    return this.paymentMethodService.getUserPaymentMethods().pipe(
      tap(paymentMethods => {
        const defaultPaymentMethod = paymentMethods.find(paymentMethod => paymentMethod.isDefault);

        this.selectPaymentMethod(this.selectedPaymentMethod || defaultPaymentMethod || paymentMethods[0]);
      })
    );
  }

  private checkCanAcceptPayment() {
    return this.paymentService.checkCanAcceptPayment(PaymentProviderNameEnum.Stripe);
  }

  private loadStripeInstance$(): Observable<Stripe> {
    return this.paymentProviderAccountService.getPaymentProviderAccount(PaymentProviderNameEnum.Stripe).pipe(
      filter((account) => account?.status === PaymentProviderAccountStatusEnum.Active),
      first(),
      switchMap<PaymentProviderAccount, Promise<Stripe>>((account) =>
        loadStripe(environment.stripe.publishableKey, { stripeAccount: account.providerAccountId })
      ),
      tap((stripe) => (this.stripe = stripe))
    );
  }

  private initialize(): Observable<unknown> {
    return this.getPaymentMethods().pipe(
      take(1),
      tap(methods => {
        this.paymentMethodsStateSubject.next({
          methods,
          loaded: true,
        });

        if (methods.length) {
          this.stripeElementReadySubject.next(false);
          this.destroyStripeElements();
        }
      }),
      catchError(() => {
        this.paymentMethodsStateSubject.next({
          methods: [],
          loaded: true,
        });
        this.stripeElementReadySubject.next(false);
        return of([]);
      }),
    );
  }

  private watchPaymentMethodInitialization(): void {
    this.canAcceptPayment$.pipe(
      filter(v => Boolean(v)),
      switchMap(() => this.initialize()),
      untilDestroyed(this)
    ).subscribe();
  }

  private watchPreparedPaymentElement(): void {
    this.stripeElementContext$.pipe(
      switchMap((context) => this.initializeNewPayment$(context)),
      untilDestroyed(this),
    ).subscribe();
  }

  private initializeNewPayment$(context: StripeElementContext): Observable<unknown> {
    this.stripeElementReadySubject.next(false);

    return forkJoin([
      this.loadStripeInstance$(),
      this.prepareStripePayment$(context.order),
    ]).pipe(
      combineLatestWith(this.activeUser$.pipe(first())),
      tap(([[stripe, preparedPayment], user]) => {
        this.latestPreparedPaymentIdSubject.next(preparedPayment.paymentId);
        this.createStripeElements(stripe, preparedPayment.clientSecret, user.email);
        this.stripeElementReadySubject.next(true);
      }),
      catchError((error) => {
        this.latestPreparedPaymentIdSubject.next(null);
        this.stripeElementReadySubject.next(false);
        this.destroyStripeElements();
        this.handlePaymentPreparationError(error);

        return EMPTY;
      }),
    );
  }

  private createStripeElements(stripe: Stripe, clientSecret: string, userEmail: string): void {
    this.stripe = stripe;
    this.destroyStripeElements();
    this.elements = stripe.elements({ clientSecret });

    const linkAuthElement = this.elements.create('linkAuthentication', {
      defaultValues: { email: userEmail }
    });

    const paymentElement = this.elements.create('payment', {
      layout: 'tabs',
      fields: { billingDetails: 'never' }
    });

    linkAuthElement.mount(`#${this.linkAuthenticationElementId}`);
    paymentElement.mount(`#${this.paymentElementId}`);
  }

  private prepareStripePayment$(order: Order, paymentMethodId?: string): Observable<{ clientSecret: string; paymentId: string | null }> {
    return this.checkoutFlowFacade.preparePayment$({
      orderId: order.id,
      provider: PaymentProviderNameEnum.Stripe,
      paymentMethodId: paymentMethodId ?? null,
    }).pipe(
      map((preparedPayment) => {
        if (!preparedPayment?.clientSecret) {
          throw new Error('Missing Stripe client secret.');
        }

        return {
          clientSecret: preparedPayment.clientSecret,
          paymentId: preparedPayment.paymentId ?? null,
        };
      }),
    );
  }

  private confirmPreparedPayment$(clientSecret?: string | null): Observable<PaymentIntentResult> {
    if (!clientSecret) {
      return EMPTY;
    }

    return this.confirmPayment$(clientSecret);
  }

  private buildPreparedPaymentElementSignature(order: Order, promotionId: string | null): string {
    return [
      order.id,
      order.updatedAt ?? '',
      order.payment?.status ?? '',
      promotionId ?? '',
    ].join('|');
  }

  private createStripeElementContext(order: Order, promotionId: string | null): StripeElementContext {
    return {
      order,
      promotionId,
      signature: this.buildPreparedPaymentElementSignature(order, promotionId),
    };
  }

  private handlePaymentPreparationError(error: any): void {
    const graphQLErrors = error?.graphQLErrors ?? [];
    const messages = this.errorService.getValidationErrorMessages(graphQLErrors);

    if (messages.length) {
      this.snackBarService.pushErrorAlert(messages[0]);
      return;
    }

    this.snackBarService.pushErrorMessage();
  }

  private destroyStripeElements(): void {
    try {
      const linkAuthEl = document.getElementById(this.linkAuthenticationElementId);
      const paymentEl = document.getElementById(this.paymentElementId);

      if (linkAuthEl) {
        linkAuthEl.innerHTML = '';
      }

      if (paymentEl) {
        paymentEl.innerHTML = '';
      }
    } catch (e) {
      // Stripe elements may already be unmounted.
    }

    this.elements = null;
  }

  private isPaymentIntentInFlight(status: PaymentIntentResult['paymentIntent'] extends infer T
    ? T extends { status: infer S }
      ? S
      : never
    : never): boolean {
    return status === 'processing' ||
      status === 'requires_action' ||
      status === 'requires_capture' ||
      status === 'requires_confirmation';
  }

  private setLoading(isLoading: boolean) {
    this._loading$.next(isLoading);
  }

  ngOnDestroy(): void {
    this.destroyStripeElements();
    this._loading$.complete();
    this.paymentMethodsStateSubject.complete();
    this.stripeElementReadySubject.complete();
  }
}
